import { QueryClient, useQuery } from '@tanstack/react-query';
import { useAppContext } from 'context/hooks';
import {
  getPatientData,
  getTotalActivePatients,
  getTotalPatients,
  getUserData,
  logoutCurrentUser,
  userLoader,
} from 'services/user';
import { queryKeys, USER_QUERY_STALE_TIME } from 'utils/query';
import { redirectToLogin } from 'utils/location';
import { ObjectId } from '@extrahorizon/javascript-sdk';
import { useCustomMutation } from './useCustomMutation';

export const useUserData = () =>
  useQuery({ queryKey: [queryKeys.userData], queryFn: getUserData, staleTime: USER_QUERY_STALE_TIME, retry: 0 });

export const usePatient = (options: { patientId?: string }) => {
  const { visibleGroupsIds, currentUser } = useAppContext();
  const { patientId } = options;
  return useQuery({
    queryKey: [queryKeys.patientData, patientId],
    queryFn: () => getPatientData({ patientId, visibleGroupsIds, activeGroups: currentUser?.visibleGroups }),
    enabled: !!patientId,
  });
};

export const useTotalPatients = () => {
  const { visibleGroupsIds } = useAppContext();

  return useQuery({
    queryKey: [queryKeys.totalPatients],
    queryFn: () => getTotalPatients(visibleGroupsIds),
    enabled: !!visibleGroupsIds,
  });
};

export const useTotalActivePatients = () => {
  const { visibleGroupsIds } = useAppContext();

  return useQuery({
    queryKey: [queryKeys.totalActivePatients],
    queryFn: () => getTotalActivePatients(visibleGroupsIds),
    enabled: !!visibleGroupsIds,
  });
};

export const useLogout = () => {
  return useCustomMutation({
    fn: logoutCurrentUser,
    onSuccess: { callback: redirectToLogin },
  });
};

export const getUserByIdQuery = (queryClient: QueryClient, id: string) => {
  return queryClient.fetchQuery({
    queryKey: [queryKeys.users, id],
    // User Loader combines single user request into a batched call
    queryFn: () => userLoader.load(id),
    staleTime: 1000 * 60 * 30, // 30 min
  });
};

export const useUserQuery = (id?: ObjectId) => {
  return useQuery({
    queryKey: [queryKeys.users, id],
    queryFn: () => userLoader.load(id ?? ''),
    staleTime: 1000 * 60 * 30, // 30 min,
    enabled: id !== undefined && id !== null,
  });
};
